import { htmlElements, newBlockTags, newSpanTags } from './htmlTags';
import { svgChildTags, svgChildTagsAll, svgElements } from './svgTags';

/**
 * The TINY_MCE_VERSION is the folder name inside public/wysiwyg/[TINY_MCE_VERSION]
 * releasing this way helps ensure that the client does not get a cached version
 */
export const TINY_MCE_VERSION = 'v6.3.1';

/**
 * An upcoming version of TINY_MCE_VERSION which is managed via feature flags.
 */
export const BETA_TINY_MCE_VERSION = 'v7.0.0';

// extended_valid_elements
// IMPORTANT: When adding a new attribute by specifying an existing element rule (e.g. table), the
// entire rule for that element is over-ridden so be sure to include ALL valid attributes, not just
// the one(s) you wish to add
export const extendedValidElements = [...htmlElements, ...svgElements].join(',');

// custom_elements
// important bug fix: link & style are allowed in the body via valid_children below, but they must
//   also be listed here in custom_elements (as block elements). Otherwise Tiny will add <div> tags
//   and &nbsp; characters to the html
// svg (as a span element, noted by the '~'), and svgChildTags are listed here to prevent Tiny from
//   adding extra space between tags
export const customElements = [
  'link',
  'style',
  '~svg',
  ...svgChildTags.map((tag) => `${tag}`),
  ...newBlockTags.map((tag) => `${tag}`),
  ...newSpanTags.map((tag) => `~${tag}`),
].join(',');

// VALID CHILDREN
// Allow style and link tags in the body of the html
// Note: attempting to add '+body[#text]', '+body[a]', and/or '+body[span]' has no effect
export const validChildren = [
  '+body[style]',
  '+body[link]',
  '+body[svg]',
  '+a[h1]', // customer request to be able to put a <h1> inside of an <a>
  '+a[h2]',
  '+a[h3]',
  '+a[h4]',
  '+a[h5]',
  '+a[h6]',
  `svg[${svgChildTagsAll.join('|')}]`,
].join(',');

/**
 * TinyMCE will only encode characters higher than \u007E (126 in unicode).
 * (decimal: 126, UTF-16(hex): 0x007E => decimal: 8364, UTF-16(hex): 0x20AC)
 * @see https://www.tiny.cloud/docs/tinymce/6/content-filtering/#entities
 */
const TINY_MCE_ENTITIES =
  '160,nbsp,161,iexcl,162,cent,163,pound,164,curren,165,yen,166,brvbar,' +
  '167,sect,168,uml,169,copy,170,ordf,171,laquo,172,not,173,shy,174,' +
  'reg,175,macr,176,deg,177,plusmn,178,sup2,179,sup3,180,acute,181,' +
  'micro,182,para,183,middot,184,cedil,185,sup1,186,ordm,187,raquo,188,' +
  'frac14,189,frac12,190,frac34,191,iquest,192,Agrave,193,Aacute,194,' +
  'Acirc,195,Atilde,196,Auml,197,Aring,198,AElig,199,Ccedil,200,Egrave,' +
  '201,Eacute,202,Ecirc,203,Euml,204,Igrave,205,Iacute,206,Icirc,207,' +
  'Iuml,208,ETH,209,Ntilde,210,Ograve,211,Oacute,212,Ocirc,213,Otilde,' +
  '214,Ouml,215,times,216,Oslash,217,Ugrave,218,Uacute,219,Ucirc,220,' +
  'Uuml,221,Yacute,222,THORN,223,szlig,224,agrave,225,aacute,226,acirc,' +
  '227,atilde,228,auml,229,aring,230,aelig,231,ccedil,232,egrave,233,' +
  'eacute,234,ecirc,235,euml,236,igrave,237,iacute,238,icirc,239,iuml,' +
  '240,eth,241,ntilde,242,ograve,243,oacute,244,ocirc,245,otilde,246,' +
  'ouml,247,divide,248,oslash,249,ugrave,250,uacute,251,ucirc,252,uuml,' +
  '253,yacute,254,thorn,255,yuml,402,fnof,913,Alpha,914,Beta,915,Gamma,' +
  '916,Delta,917,Epsilon,918,Zeta,919,Eta,920,Theta,921,Iota,922,Kappa,' +
  '923,Lambda,924,Mu,925,Nu,926,Xi,927,Omicron,928,Pi,929,Rho,931,' +
  'Sigma,932,Tau,933,Upsilon,934,Phi,935,Chi,936,Psi,937,Omega,945,' +
  'alpha,946,beta,947,gamma,948,delta,949,epsilon,950,zeta,951,eta,952,' +
  'theta,953,iota,954,kappa,955,lambda,956,mu,957,nu,958,xi,959,' +
  'omicron,960,pi,961,rho,962,sigmaf,963,sigma,964,tau,965,upsilon,966,' +
  'phi,967,chi,968,psi,969,omega,977,thetasym,978,upsih,982,piv,8226,' +
  'bull,8230,hellip,8242,prime,8243,Prime,8254,oline,8260,frasl,8472,' +
  'weierp,8465,image,8476,real,8482,trade,8501,alefsym,8592,larr,8593,' +
  'uarr,8594,rarr,8595,darr,8596,harr,8629,crarr,8656,lArr,8657,uArr,' +
  '8658,rArr,8659,dArr,8660,hArr,8704,forall,8706,part,8707,exist,8709,' +
  'empty,8711,nabla,8712,isin,8713,notin,8715,ni,8719,prod,8721,sum,' +
  '8722,minus,8727,lowast,8730,radic,8733,prop,8734,infin,8736,ang,' +
  '8743,and,8744,or,8745,cap,8746,cup,8747,int,8756,there4,8764,sim,' +
  '8773,cong,8776,asymp,8800,ne,8801,equiv,8804,le,8805,ge,8834,sub,' +
  '8835,sup,8836,nsub,8838,sube,8839,supe,8853,oplus,8855,otimes,8869,' +
  'perp,8901,sdot,8968,lceil,8969,rceil,8970,lfloor,8971,rfloor,9001,' +
  'lang,9002,rang,9674,loz,9824,spades,9827,clubs,9829,hearts,9830,' +
  'diams,338,OElig,339,oelig,352,Scaron,353,scaron,376,Yuml,710,circ,' +
  '732,tilde,8194,ensp,8195,emsp,8201,thinsp,8204,zwnj,8205,zwj,8206,' +
  'lrm,8207,rlm,8211,ndash,8212,mdash,8216,lsquo,8217,rsquo,8218,sbquo,' +
  '8220,ldquo,8221,rdquo,8222,bdquo,8224,dagger,8225,Dagger,8240,' +
  'permil,8249,lsaquo,8250,rsaquo,8364,euro';

type EntityElement = {
  unicode: number;
  name: string;
};

// utility function to convert Tiny's entities into an array for manipulation
const getTinyEntityArray = (entityPairsCsv: string) => {
  const entityPairs = entityPairsCsv.split(',');
  const result: EntityElement[] = [];
  for (let i = 0; i < entityPairs.length; i += 2) {
    const pair = entityPairs.slice(i, i + 2);
    result.push({ unicode: parseInt(pair[0], 10), name: pair[1] });
  }
  return result;
};

const tinyEntityArray = getTinyEntityArray(TINY_MCE_ENTITIES);
const currencyEntities = [162, 163, 164, 165, 8364]; // currency entities existing in Tiny's list

// create a new list of entities, excluding the ones Iterable does not want to convert
export const iterableFilteredEntities = tinyEntityArray.filter((entity) => {
  return ![...currencyEntities].includes(entity.unicode);
});

// reconstruct the entity list in Tiny's format
export const entities = iterableFilteredEntities
  .map((entity) => {
    return `${entity.unicode},${entity.name}`;
  })
  .join(',');

/**
 * TODO: maybe add svg?
 * @see https://www.tiny.cloud/docs/configure/file-image-upload/#images_file_types
 */
export const TINY_MCE_IMAGE_FILE_TYPES = 'jpeg,jpg,png,gif';

/**
 * recommended invalid elements
 * export const TINY_MCE_INVALID_ELEMENTS = 'applet,base,basefont,canvas,command,dialog,embed,iframe,isindex,keygen,noscript,object,output,param';
 * invalid elements for parity with ckEditor (iframe,noscript)
 * @see https://www.tiny.cloud/docs-3x/reference/Configuration3x/Configuration3x@invalid_elements/
 */
export const TINY_MCE_INVALID_ELEMENTS = 'applet,iframe,noscript';

/**
 * Note: the default id will be assigned to the id of a textarea that is not displayed
 * (display: none), but will have (as its .value) the initial html. The id will also be assigned to
 * the id attribute (undocumented) of the iframe containing the editor with an "ifr" suffix
 * (example: 'content-editor-wysiwyg_ifr').
 * test scenario: `container.querySelector('#content-editor-wysiwyg_ifr').contentDocument.body.innerHTML`
 */
export const WYSIWYG_DEFAULT_ID = 'content-editor-wysiwyg';
